/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import net.sergeych.bipack.BipackEncoder
import net.sergeych.bipack.decodeFromBipack
import net.sergeych.mp_tools.decodeBase64Compact
import net.sergeych.mp_tools.encodeToBase64Compact

@Serializable
sealed class UniversalKey : KeyInstance {

    abstract val keyBytes: UByteArray

    @Transient
    open val magic: KeysmagicNumber = KeysmagicNumber.Unknown

    /**
     * Key ID positively identify key from the point of view of _decrypting or verifying_. So matching [VerifyingKey]
     * and [SigningKey] will have the same id, same as matching [EncryptingPublicKey] and [DecryptingSecretKey].
     *
     * KeyId is based on [BinaryId] which includes checksum (crc8) and magick number for additional security,
     * see [KeysmagicNumber].
     *
     * Also "public" keys can be restored from id using [BinaryId.asPublicKey] and [BinaryId.asVerifyingKey].
     */
    override val id by lazy { KeyId(magic, keyBytes, null) }

    // Important: id can be overridden, so we use it, not magic:
    open val label by lazy { id.keymagic.label }

    override fun equals(other: Any?): Boolean {
        return other is UniversalKey && other.keyBytes contentEquals keyBytes
    }

    override fun hashCode(): Int {
        return keyBytes.contentHashCode()
    }

    override fun toString(): String {
        val pwdSign = if (id.kdp != null) '*' else '#'
        return "🗝${label}$pwdSign$id"
    }

    companion object {
        fun newSecretKey() = DecryptingSecretKey.new()
        fun newSigningKey() = SigningSecretKey.new()

        @Suppress("unused")
        fun newSymmetricKey() = SymmetricKey.new()

        /**
         * Parse all known string representations of the universal key
         * @throws IllegalArgumentException if it can't parse any key.
         */
        fun parseString(text: String): UniversalKey {
            val s = text.trim()
            return when {
                s.startsWith("\uD83D\uDDDDpub#") || s.startsWith("pub#") ->
                    EncryptingPublicKey.parse(s)
                s.startsWith("\uD83D\uDDDDver#") || s.startsWith("ver#") ->
                    VerifyingPublicKey.parse(s)
                else -> {
                    s.decodeBase64Compact().decodeFromBipack<UniversalKey>()
                }
            }
        }

    }
}

inline fun <reified T : UniversalKey> T.asString() =
    BipackEncoder.encode<T>(this).encodeToBase64Compact()


open class IllegalSignatureException(text: String = "signed data is tampered or signature is corrupted") :
    IllegalStateException(text)

class ExpiredSignatureException(text: String) : IllegalSignatureException(text)